import { supabase } from '../lib/supabase';

export const projectBoardService = {
  // Get all project board items with optional filters
  async getProjects(filters = {}) {
    try {
      let query = supabase?.from('project_board_items')?.select(`
          *,
          created_by_profile:user_profiles!project_board_items_created_by_fkey(
            id,
            first_name,
            last_name,
            email
          ),
          project_tasks(
            id,
            title,
            is_completed,
            completion_percentage
          )
        `)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }
      if (filters?.phase) {
        query = query?.eq('phase', filters?.phase);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  // Get single project with all details
  async getProjectById(projectId) {
    try {
      const { data, error } = await supabase?.from('project_board_items')?.select(`
          *,
          created_by_profile:user_profiles!project_board_items_created_by_fkey(
            id,
            first_name,
            last_name,
            email,
            avatar_url
          ),
          project_tasks(
            *,
            assigned_to_profile:user_profiles(
              id,
              first_name,
              last_name,
              email
            )
          ),
          project_discussions(
            *,
            created_by_profile:user_profiles(
              id,
              first_name,
              last_name,
              email,
              avatar_url
            )
          )
        `)?.eq('id', projectId)?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching project details:', error);
      throw error;
    }
  },

  // Create new project
  async createProject(projectData) {
    try {
      const { data: userData } = await supabase?.auth?.getUser();
      
      const { data, error } = await supabase?.from('project_board_items')?.insert([
          {
            ...projectData,
            created_by: userData?.user?.id
          }
        ])?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  // Update project
  async updateProject(projectId, updates) {
    try {
      const { data, error } = await supabase?.from('project_board_items')?.update(updates)?.eq('id', projectId)?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  // Delete project
  async deleteProject(projectId) {
    try {
      const { error } = await supabase?.from('project_board_items')?.delete()?.eq('id', projectId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  // Project Tasks Operations
  async getProjectTasks(projectId) {
    try {
      const { data, error } = await supabase?.from('project_tasks')?.select(`
          *,
          assigned_to_profile:user_profiles(
            id,
            first_name,
            last_name,
            email
          )
        `)?.eq('project_id', projectId)?.order('sort_order', { ascending: true });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching project tasks:', error);
      throw error;
    }
  },

  async createTask(taskData) {
    try {
      const { data, error } = await supabase?.from('project_tasks')?.insert([taskData])?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  },

  async updateTask(taskId, updates) {
    try {
      const { data, error } = await supabase?.from('project_tasks')?.update(updates)?.eq('id', taskId)?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  },

  async deleteTask(taskId) {
    try {
      const { error } = await supabase?.from('project_tasks')?.delete()?.eq('id', taskId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  },

  // Project Discussions Operations
  async getProjectDiscussions(projectId) {
    try {
      const { data, error } = await supabase?.from('project_discussions')?.select(`
          *,
          created_by_profile:user_profiles(
            id,
            first_name,
            last_name,
            email,
            avatar_url
          )
        `)?.eq('project_id', projectId)?.order('is_pinned', { ascending: false })?.order('created_at', { ascending: false });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching project discussions:', error);
      throw error;
    }
  },

  async createDiscussion(discussionData) {
    try {
      const { data: userData } = await supabase?.auth?.getUser();

      const { data, error } = await supabase?.from('project_discussions')?.insert([
          {
            ...discussionData,
            created_by: userData?.user?.id
          }
        ])?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error creating discussion:', error);
      throw error;
    }
  },

  async updateDiscussion(discussionId, updates) {
    try {
      const { data, error } = await supabase?.from('project_discussions')?.update(updates)?.eq('id', discussionId)?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating discussion:', error);
      throw error;
    }
  },

  async deleteDiscussion(discussionId) {
    try {
      const { error } = await supabase?.from('project_discussions')?.delete()?.eq('id', discussionId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting discussion:', error);
      throw error;
    }
  }
};